import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Stack,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { delftBlue } from "@/theme/colors";
import FormInput from "@/components/forms/FormInput";
import FormTextArea from "@/components/forms/FormTextArea";
import FormSelect from "@/components/forms/FormSelect";
import MultiSelectField from "@/components/forms/FormMultiSelect";
import {
  useGetDocumentQuery,
  useUploadDocumentMutation,
  useUpdateDocumentMutation,
  useGetActivitiesQuery,
  useGetCompaniesQuery,
} from "@/app/features/dashboard/dashboardApiSlice";
import { useSelector } from "react-redux";
import { selectUser } from "@/app/features/auth/authSlice";
import Roles from "@/constants/roles";

const DocumentCategory = {
  ACTIVITY: "activity",
  OTHER: "other",
};

const categoryOptions = [
  { code: DocumentCategory.ACTIVITY, name: "Activity" },
  { code: DocumentCategory.OTHER, name: "Other" },
];

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .required("Document title is required")
    .min(2, "Title must be at least 2 characters")
    .max(200, "Title cannot exceed 200 characters"),
  description: Yup.string()
    .max(100, "Description cannot exceed 100 characters")
    .optional(),
  file: Yup.mixed()
    .nullable()
    .test("fileRequired", "File is required", function (value) {
      const { parent } = this;
      // File is only required when creating new document (not in edit mode)
      if (parent.isEditMode) {
        return true; // In edit mode, file is optional
      }
      // In create mode, file is required
      return !!value;
    })
    .test("fileSize", "File size must be less than 50MB", function (value) {
      if (!value) return true;
      if (typeof value === "string") return true; // Existing file URL
      return value.size <= 50 * 1024 * 1024;
    }),
  activityId: Yup.string().nullable().optional(),
  category: Yup.string()
    .oneOf(
      [DocumentCategory.ACTIVITY, DocumentCategory.OTHER],
      "Invalid category",
    )
    .required("Category is required"),
  tags: Yup.string()
    .nullable()
    .optional()
    .max(50, "Tags cannot exceed 50 characters"),
  companyIds: Yup.array().nullable().optional(),
  isEditMode: Yup.boolean().optional(),
});

const DocumentFormPage = () => {
  console.log("DocumentFormPage component mounted");
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const isEditMode = !!id;
  const [formSubmitted, setFormSubmitted] = useState(false);
  console.log("isEditMode:", isEditMode, "id:", id);

  const {
    data: documentData,
    isLoading: isLoadingDocument,
    error: documentError,
    refetch: refetchDocument,
  } = useGetDocumentQuery(id, {
    skip: !isEditMode,
  });

  // Refetch document data when navigating to edit form to ensure fresh data
  useEffect(() => {
    if (isEditMode && id) {
      refetchDocument();
    }
  }, [id, isEditMode, refetchDocument]);

  const { data: activitiesData, isLoading: isLoadingActivities } =
    useGetActivitiesQuery({ page: 1, limit: 1000 });
  const { data: companiesData, isLoading: isLoadingCompanies } =
    useGetCompaniesQuery({ page: 1, limit: 1000 });

  const [uploadDocument, { isLoading: isUploading }] =
    useUploadDocumentMutation();
  const [updateDocument, { isLoading: isUpdating }] =
    useUpdateDocumentMutation();

  const activities =
    activitiesData?.data?.activities || activitiesData?.activities || [];
  const activityOptions = activities.map((activity) => ({
    code: activity._id?.toString() || activity.id?.toString(),
    name: activity.name || "Unknown Activity",
  }));

  const companies =
    companiesData?.data?.companies || companiesData?.companies || [];
  const companyOptions = companies.map((company) => ({
    code: company._id?.toString() || company.id?.toString(),
    name: company.name || "Unknown Company",
  }));

  const document = documentData?.data?.document || documentData?.document;

  // CDN URL mapping for current document view
  const S3_BASE_URL =
    "https://s3.ap-southeast-2.amazonaws.com/op-docx-bucket-prod";
  const CLOUDFRONT_URL = "https://d3hjliepkppdbb.cloudfront.net";

  const getTransformedFileUrl = (fileUrl) => {
    if (!fileUrl) return "";
    if (fileUrl.includes(S3_BASE_URL)) {
      return fileUrl.replace(S3_BASE_URL, CLOUDFRONT_URL);
    }
    return fileUrl.startsWith("http") ? fileUrl : `${CLOUDFRONT_URL}${fileUrl}`;
  };

  // Determine which companies can be selected based on user role
  const getAvailableCompanies = () => {
    if (user?.role === Roles.SUPER_ADMIN) {
      return companyOptions;
    } else if (user?.role === Roles.ADMIN && user?.companyId) {
      const userCompanyId = user.companyId.toString();
      return companyOptions.filter((company) => company.code === userCompanyId);
    }
    return [];
  };

  // Filter companies based on category and activity selection
  const getFilteredCompanies = (category, activityId) => {
    const baseCompanies = getAvailableCompanies();

    // If category is "Other", return all available companies
    if (category === DocumentCategory.OTHER) {
      return baseCompanies;
    }

    // If category is "Activity"
    if (category === DocumentCategory.ACTIVITY) {
      // If no activity is selected, return empty array
      if (!activityId) {
        return [];
      }

      // Find the selected activity
      const selectedActivity = activities.find(
        (activity) =>
          (activity._id?.toString() || activity.id?.toString()) === activityId,
      );

      // If activity not found, return empty array
      if (!selectedActivity) {
        return [];
      }

      // Get companyIds from the selected activity
      const activityCompanyIds = selectedActivity.companyIds || [];
      const activityCompanyIdStrings = activityCompanyIds.map((id) => {
        // Handle populated objects with _id (old format)
        if (id && typeof id === "object" && id._id !== undefined) {
          return id._id.toString();
        }
        // Handle populated objects with id (new format - transformed by backend)
        if (id && typeof id === "object" && id.id !== undefined) {
          return id.id.toString();
        }
        // Handle string IDs
        return id.toString();
      });

      // Filter companies to only include those enrolled in the selected activity
      return baseCompanies.filter((company) =>
        activityCompanyIdStrings.includes(company.code),
      );
    }

    // Default: return all available companies
    return baseCompanies;
  };

  const initialValues = {
    title: document?.title || "",
    description: document?.description || "",
    file: null,
    activityId:
      document?.activityId?._id?.toString() ||
      document?.activityId?.id?.toString() ||
      document?.activityId?.toString() ||
      "",
    category: document?.category || DocumentCategory.OTHER,
    tags: document?.tags?.join(", ") || "",
    companyIds:
      document?.companyIds?.map((id) => {
        // Handle populated objects with _id (old format)
        if (id && typeof id === "object" && id._id !== undefined) {
          return id._id.toString();
        }
        // Handle populated objects with id (new format - transformed by backend)
        if (id && typeof id === "object" && id.id !== undefined) {
          return id.id.toString();
        }
        // Handle string IDs
        return id.toString();
      }) || (user?.companyId ? [user.companyId.toString()] : []),
    isEditMode,
  };

  const handleFormSubmit = async (values, { setSubmitting, setStatus }) => {
    console.log("Form submitted with values:", values);
    console.log("Is edit mode:", isEditMode);
    try {
      if (isEditMode) {
        // Update document
        const updatePayload = {
          title: values.title,
          description: values.description || "",
          category: values.category,
          tags: values.tags
            ? values.tags
                .split(",")
                .map((tag) => tag.trim())
                .filter((tag) => tag.length > 0)
            : [],
        };
        console.log("Update payload before activityId check:", updatePayload);

        // Only update activityId if it changed
        if (values.activityId) {
          updatePayload.activityId = values.activityId;
        } else if (values.activityId === "" && document?.activityId) {
          // Remove activity assignment
          updatePayload.activityId = null;
        }

        // Only update companyIds if user is super admin.
        // Empty array means "global" (accessible to all companies).
        // If companyIds is not provided (undefined), backend keeps existing assignment.
        if (
          user?.role === Roles.SUPER_ADMIN &&
          Array.isArray(values.companyIds)
        ) {
          updatePayload.companyIds = values.companyIds;
        }

        console.log("Final update payload:", updatePayload);
        console.log("Document ID:", id);
        console.log("About to call updateDocument...");
        const { error } = await updateDocument({ id, ...updatePayload });
        console.log("updateDocument result - error:", error);
        if (error) {
          setStatus({
            success: false,
            message: error?.data?.message || "Failed to update document",
          });
          setSubmitting(false);
          return;
        }
      } else {
        // Upload new document
        if (!values.file) {
          setStatus({ success: false, message: "File is required" });
          setSubmitting(false);
          return;
        }

        const formData = new FormData();
        formData.append("file", values.file);
        formData.append("title", values.title);
        if (values.description) {
          formData.append("description", values.description);
        }
        if (values.activityId) {
          formData.append("activityId", values.activityId);
        }
        if (values.category) {
          formData.append("category", values.category);
        }
        if (values.tags) {
          formData.append("tags", values.tags);
        }
        if (values.companyIds && values.companyIds.length > 0) {
          values.companyIds.forEach((companyId) => {
            formData.append("companyIds[]", companyId);
          });
        }

        const { error } = await uploadDocument(formData);
        if (error) {
          setStatus({
            success: false,
            message: error?.data?.message || "Failed to upload document",
          });
          setSubmitting(false);
          return;
        }
      }

      navigate("/documents", { state: { refresh: true } });
    } catch (error) {
      console.error("Failed to submit document:", error);
      const message =
        error?.data?.message ||
        error?.error ||
        error?.message ||
        (isEditMode
          ? "Failed to update document"
          : "Failed to upload document");
      setStatus({ success: false, message });
      setSubmitting(false);
    }
  };

  if (isEditMode && isLoadingDocument) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isEditMode && documentError) {
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="error">
          {documentError?.data?.message || "Failed to load document"}
        </Alert>
        <Button onClick={() => navigate("/documents")} sx={{ mt: 2 }}>
          Back to Documents
        </Button>
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        alignSelf: "stretch",
        minHeight: "100vh",
        paddingTop: { xs: 2, sm: 2.5, md: 3 },
        paddingLeft: { xs: 1.5, sm: 2, md: 3 },
        paddingRight: { xs: 1.5, sm: 2, md: 3 },
        paddingBottom: { xs: 2, sm: 2.5, md: 3 },
      }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize
        // validateOnChange={true}
        validateOnBlur={true}
      >
        {({
          handleSubmit,
          isSubmitting,
          status,
          values,
          setFieldValue,
          errors,
          touched,
          isValid,
          setFieldTouched,
        }) => {
          // Log only when there are errors to reduce console noise
          if (Object.keys(errors).length > 0) {
            console.log("Formik validation errors:", errors);
          }

          return (
            <form
              onSubmit={(e) => {
                console.log("Form onSubmit event triggered");
                e.preventDefault();
                setFormSubmitted(true);
                // Mark file as touched before validation
                setFieldTouched("file", true);
                console.log("Calling handleSubmit");
                handleSubmit(e);
              }}
            >
              <Stack spacing={3}>
                {/* Header */}
                <Box>
                  <Typography
                    sx={{
                      color: delftBlue[500],
                      fontSize: { xs: 22, sm: 24, md: 28 },
                      fontFamily: "Audiowide, sans-serif",
                      fontWeight: 400,
                      lineHeight: 1.29,
                      marginBottom: 1,
                    }}
                  >
                    {isEditMode ? "Edit Document" : "Upload New Document"}
                  </Typography>
                  <Typography
                    sx={{
                      color: delftBlue[500],
                      fontSize: { xs: 13, sm: 14, md: 16 },
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: 400,
                      lineHeight: 1.5,
                    }}
                  >
                    {isEditMode
                      ? "Update document details"
                      : "Upload a new document"}
                  </Typography>
                </Box>

                {/* Error Message */}
                {status?.message && (
                  <Alert severity="error">{status.message}</Alert>
                )}

                {/* Validation Errors
                {Object.keys(errors).length > 0 && (
                  <Alert severity="warning">
                    <strong>Validation Errors:</strong>
                    <pre>{JSON.stringify(errors, null, 2)}</pre>
                  </Alert>
                )} */}

                {/* Form Fields */}
                <Stack spacing={2}>
                  <FormInput
                    name="title"
                    label="Document Title"
                    placeholder="e.g., Safety Guidelines"
                  />

                  <FormTextArea
                    name="description"
                    label="Description"
                    placeholder="Enter document description"
                  />

                  {/* File Upload - only for new documents */}
                  {!isEditMode && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        File Upload *
                      </Typography>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.rtf,.md,image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setFieldValue("file", file);
                            setFieldTouched("file", true);
                          }
                        }}
                        onBlur={() => {
                          setFieldTouched("file", true);
                        }}
                      />
                      {values.file && (
                        <Typography
                          variant="caption"
                          sx={{ mt: 1, display: "block" }}
                        >
                          Selected: {values.file.name}
                        </Typography>
                      )}
                      {/* Only show file error after form submission attempt */}
                      {formSubmitted && touched.file && errors.file && (
                        <Typography
                          variant="caption"
                          sx={{
                            mt: 0.5,
                            display: "block",
                            color: "error.main",
                          }}
                        >
                          {errors.file}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {isEditMode && document?.fileUrl && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Current File
                      </Typography>
                      <Button
                        variant="outlined"
                        href={getTransformedFileUrl(document.fileUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View Current Document
                      </Button>
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 1,
                          display: "block",
                          color: "text.secondary",
                        }}
                      >
                        Note: File cannot be changed after upload. To use a
                        different file, create a new document.
                      </Typography>
                    </Box>
                  )}

                  <FormSelect
                    name="category"
                    label="Category"
                    items={categoryOptions}
                    onChange={(e) => {
                      const newCategory = e.target.value;
                      setFieldValue("category", newCategory);

                      // If switching to Activity category, clear activityId if needed
                      if (newCategory === DocumentCategory.ACTIVITY) {
                        // Clear companyIds when switching to Activity category
                        // They will be filtered based on selected activity
                        setFieldValue("companyIds", []);
                      }

                      // Validate and filter companyIds based on new category
                      const filteredCompanies = getFilteredCompanies(
                        newCategory,
                        values.activityId,
                      );
                      const validCompanyIds = (values.companyIds || []).filter(
                        (id) => filteredCompanies.some((c) => c.code === id),
                      );
                      setFieldValue("companyIds", validCompanyIds);
                    }}
                  />

                  {values.category === DocumentCategory.ACTIVITY && (
                    <>
                      {isLoadingActivities ? (
                        <CircularProgress size={24} />
                      ) : (
                        <FormSelect
                          name="activityId"
                          label="Activity"
                          items={[
                            // { code: "", name: "None" },
                            ...activityOptions,
                          ]}
                          onChange={(e) => {
                            const newActivityId = e.target.value;
                            setFieldValue("activityId", newActivityId);

                            // Filter companyIds based on the selected activity
                            const filteredCompanies = getFilteredCompanies(
                              values.category,
                              newActivityId,
                            );
                            const validCompanyIds = (
                              values.companyIds || []
                            ).filter((id) =>
                              filteredCompanies.some((c) => c.code === id),
                            );
                            setFieldValue("companyIds", validCompanyIds);
                          }}
                        />
                      )}
                    </>
                  )}

                  {user?.role === Roles.SUPER_ADMIN && (
                    <>
                      {isLoadingCompanies ? (
                        <CircularProgress size={24} />
                      ) : (
                        <MultiSelectField
                          name="companyIds"
                          label="Companies"
                          items={getFilteredCompanies(
                            values.category,
                            values.activityId,
                          )}
                        />
                      )}
                    </>
                  )}

                  <FormInput
                    name="tags"
                    label="Tags (comma-separated)"
                    placeholder="e.g., safety, guidelines, training"
                  />
                </Stack>

                {/* Action Buttons */}
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={() => navigate("/documents")}
                    disabled={isSubmitting || isUploading || isUpdating}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || isUploading || isUpdating}
                    onClick={() => {
                      console.log("Button clicked!");
                      console.log("Current errors:", errors);
                      console.log("Is valid:", isValid);
                    }}
                    sx={{
                      backgroundColor: delftBlue[500],
                      "&:hover": {
                        backgroundColor: delftBlue[600],
                      },
                    }}
                  >
                    {isSubmitting || isUploading || isUpdating ? (
                      <CircularProgress size={24} />
                    ) : isEditMode ? (
                      "Update Document"
                    ) : (
                      "Upload Document"
                    )}
                  </Button>
                </Stack>
              </Stack>
            </form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default DocumentFormPage;
